// Copyright (c) 2020 embyt GmbH. All rights reserved.

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Link } from "react-router-dom";

import { MainPanel } from "types";
import { IState } from "states/states";
import { ActionType, IAction, IActionChangeOutput, raiseAction } from "actions";
import { Bus, IModule, IOutput, OutputCmd, OutputMode } from "parameter";
import { IStateDataOutput } from "states/datastates";
import { round } from "utils";

import OutputModel, { IOState } from "model/output";

import { ReactComponent as IconOff } from "static/iconOff.svg";
import { ReactComponent as IconOffOverride } from "static/iconOffOverride.svg";
import { ReactComponent as IconOn } from "static/iconOn.svg";
import { ReactComponent as IconOnOverride } from "static/iconOnOverride.svg";
import { ReactComponent as IconUnknown } from "static/iconUnknown.svg";
import { ReactComponent as IconEdit } from "static/iconEdit.svg";
import { makeCommandSeqNr, tabClickHandler } from "guiutils";

interface IOutputExtProps {
  index: number;
  isEditable: boolean;
  showModule: boolean;
}

interface IOutputProps {
  parameter: IOutput;
  value: IStateDataOutput;
  modules: IModule[] | undefined;
}

const mapStateToProps = (state: IState, ownProps: IOutputExtProps) => {
  const props: IOutputProps = {
    parameter: state.parameter!.output[ownProps.index],
    value: state.data.output[ownProps.index],
    modules: state.parameter?.module,
  };
  return props;
};

interface IOutputDispatch {
  onEdit: () => void;
  onToggle: (cmd: OutputCmd) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IOutputExtProps,
) => {
  const dispatchProps: IOutputDispatch = {
    onEdit: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_OUTPUT, ownProps.index));
    },
    onToggle: (cmd: OutputCmd) => {
      const payload: IActionChangeOutput = {
        commandSeqNr: makeCommandSeqNr(),
        index: ownProps.index,
        cmd,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_OUTPUT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  edit: {
    id: "change value",
  },
  analog: {
    id: "analog",
  },
  pwm: {
    id: "control value",
  },
  on: {
    id: "On",
  },
  off: {
    id: "Off",
  },
  override: {
    id: "override",
  },
});

class OutputComponent extends React.Component<
  IOutputProps & IOutputExtProps & IOutputDispatch & WrappedComponentProps
> {
  public render() {
    const output = new OutputModel(this.props.parameter, this.props.value);
    const hasValue = output.parameter.mode === OutputMode.ANALOG;
    const isEditable =
      this.props.isEditable &&
      output.parameter.webCtrl &&
      output.parameter.mode !== OutputMode.ANALOG;
    const module =
      this.props.parameter.bus === Bus.MODULE
        ? this.props.modules?.find((curItem) => curItem.id === this.props.parameter.module)
        : undefined;

    return (
      <tr className="outputitem">
        <td>{this.props.parameter.name}</td>
        {this.props.showModule && (
          <td className="optional">
            <Link to={`../${MainPanel.MODULES}`} style={{ textDecoration: "none" }}>
              {module?.name}
            </Link>
          </td>
        )}
        <td
          className={`icon-col ${hasValue ? undefined : "right"} ${isEditable ? "img-link" : ""}`}
          colSpan={hasValue ? 1 : 2}
          title={this.getText(output)}
          onClick={
            isEditable
              ? () => {
                  const cmd = output.isOverride
                    ? output.state === IOState.ON
                      ? OutputCmd.OVERRIDE_OFF
                      : OutputCmd.OVERRIDE_ON
                    : output.state === IOState.OFF
                    ? OutputCmd.ON
                    : OutputCmd.OFF;
                  this.props.onToggle(cmd);
                }
              : undefined
          }
        >
          {this.getIcon(output)}
        </td>
        {hasValue && (
          <td className="value-col">
            {output.parameter.mode === OutputMode.ANALOG && output.getText()}
          </td>
        )}
        {this.props.isEditable &&
          (isEditable ? (
            <td
              tabIndex={0}
              onKeyDown={tabClickHandler}
              onClick={this.props.onEdit}
              className="edit-col img-link"
              title={this.props.intl.formatMessage(messages.edit)}
            >
              <IconEdit />
            </td>
          ) : (
            <td className="edit-col"></td>
          ))}
      </tr>
    );
  }

  private getIcon(output: OutputModel) {
    if (output.parameter.mode === OutputMode.ANALOG) return <></>;
    if (output.state === IOState.OFF) return output.isOverride ? <IconOffOverride /> : <IconOff />;
    if (output.state === IOState.ON) return output.isOverride ? <IconOnOverride /> : <IconOn />;
    return <IconUnknown />;
  }

  private getText(output: OutputModel) {
    if (output.parameter.mode === OutputMode.ANALOG)
      return this.props.intl.formatMessage(messages.analog);
    if (output.parameter.mode === OutputMode.PWM)
      return `${this.props.intl.formatMessage(messages.pwm)}: ${round(output.value?.logical)}`;
    const content: string[] = [];
    if (output.isOverride) content.push(this.props.intl.formatMessage(messages.override));
    if (output.state === IOState.OFF) content.push(this.props.intl.formatMessage(messages.off));
    if (output.state === IOState.ON) content.push(this.props.intl.formatMessage(messages.on));
    return content.join(" ");
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(OutputComponent));
